import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import RoleEditor from '@shared/components/organization/role/RoleEditor';
import { extractRoleProperties } from '@shared/hooks/helpers/OrganizationalHelpers';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import OrganizationMemberRole from '@shared/models/organizations/OrganizationMemberRole';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MemberDetailStack from './MemberDetailStack';

interface IMemberEditDialogProps {
    open: boolean;
    onClose: () => void;
    member: OrganizationMemberModel;
    myRole: OrganizationMemberRole;
}

function MemberEditDialog({ open, onClose, member, myRole }: Readonly<IMemberEditDialogProps>) {
    const { t } = useTranslation();

    const [editMember, setEditMember] = useState<OrganizationMemberModel>(member);
    const [role, setRole] = useState<OrganizationMemberRole>(extractRoleProperties(member));

    useEffect(() => {
        setEditMember(member);
        setRole(extractRoleProperties(member));
    }, [member]);

    const handleRoleChange = (updatedRole: OrganizationMemberRole) => {
        setRole(updatedRole);
        setEditMember({
            ...editMember,
            ...updatedRole,
        });
    };

    const handleSave = async () => {
        const result = await useOrganizationStore
            .getState()
            .updateMemberRole(editMember.organizationId, editMember.id, role);
        if (result?.resultType == 'Ok') {
            useAlertsStore.getState().showSuccess(
                t('common:organization.member.updated', 'Member updated successfully', {
                    value: editMember.user?.email,
                })
            );
        } else {
            useAlertsStore.getState().showError(
                t('common:organization.member.error', 'Error updating member', {
                    value: editMember.user?.email,
                })
            );
        }

        onClose();
    };

    return (
        <ContentDialog
            fullWidth
            open={open}
            onClose={onClose}
            onCancel={onClose}
            title={t('common:organization.member.edit')}
            onConfirm={handleSave}>
            <MemberDetailStack member={editMember} />
            <RoleEditor myRole={myRole} role={role} onChange={handleRoleChange} />
        </ContentDialog>
    );
}

export default MemberEditDialog;
